import type {
  PeriodSettings,
  UpdatePeriodSettings,
  RunPeriodEndRequest,
  RunPeriodEndResponse,
  SyncPeriodRequest,
  SyncPeriodResponse,
} from '@/types/settings';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

const getDataSource = (): 'demo' | 'api' => {
  const mode = localStorage.getItem('app-mode');
  if (mode === 'demo') return 'demo';
  return 'api';
};

const getDemoPeriodSettings = (): PeriodSettings => {
  const now = new Date();
  return {
    id: 'demo-period-settings',
    currentYear: now.getFullYear(),
    currentPeriod: now.getMonth() + 1,
    periodStatus: 'Open',
    autoSyncEnabled: false,
    lastSyncedAt: undefined,
    lastSyncedBy: undefined,
    lastSyncSource: undefined,
    lastPeriodEndAt: undefined,
    lastPeriodEndBy: undefined,
    createdBy: 'Demo',
    createdDate: now.toISOString(),
    modifiedBy: undefined,
    modifiedDate: undefined,
    displayPeriod: `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}`,
  };
};

class PeriodManagementService {
  private authHeader?: string;

  private getApiBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    const envUrl = import.meta.env.VITE_API_BASE_URL;
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

    const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);

    try {
      const url = new URL(baseUrl);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return baseUrl.replace(/\/$/, '');
    } catch {
      return baseUrl.replace(/\/$/, '');
    }
  }

  private async buildHeaders(options?: RequestInit): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  private async parseErrorResponse(response: Response): Promise<string> {
    let errorMessage = `API error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    return errorMessage;
  }

  private async fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const apiBaseUrl = this.getApiBaseUrl();
    const headers = await this.buildHeaders(options);

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, { ...options, headers });

      if (!response.ok) {
        throw new Error(await this.parseErrorResponse(response));
      }

      return response.status === 204 ? (null as T) : response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to API at ${apiBaseUrl}. Please ensure the backend server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  async getPeriodSettings(): Promise<PeriodSettings> {
    if (getDataSource() === 'demo') {
      return getDemoPeriodSettings();
    }

    try {
      return await this.fetchAPI<PeriodSettings>('/api/PeriodManagement');
    } catch (error) {
      console.error('[PeriodManagementService] getPeriodSettings failed:', error);
      throw error;
    }
  }

  async updatePeriodSettings(settings: UpdatePeriodSettings): Promise<PeriodSettings> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update period settings in demo mode. Please switch to API mode.');
    }

    try {
      return await this.fetchAPI<PeriodSettings>('/api/PeriodManagement', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
    } catch (error) {
      console.error('[PeriodManagementService] updatePeriodSettings failed:', error);
      throw error;
    }
  }

  async runPeriodEnd(request: RunPeriodEndRequest): Promise<RunPeriodEndResponse> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot run period end in demo mode. Please switch to API mode.');
    }

    try {
      return await this.fetchAPI<RunPeriodEndResponse>('/api/PeriodManagement/run-period-end', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('[PeriodManagementService] runPeriodEnd failed:', error);
      throw error;
    }
  }

  async syncPeriodFromFinancials(request: SyncPeriodRequest): Promise<SyncPeriodResponse> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot sync period in demo mode. Please switch to API mode.');
    }

    try {
      return await this.fetchAPI<SyncPeriodResponse>('/api/PeriodManagement/sync', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('[PeriodManagementService] syncPeriodFromFinancials failed:', error);
      throw error;
    }
  }

  async initializePeriodSettings(createdBy?: string): Promise<PeriodSettings> {
    if (getDataSource() === 'demo') {
      return getDemoPeriodSettings();
    }

    try {
      const queryString = createdBy ? `?createdBy=${encodeURIComponent(createdBy)}` : '';
      return await this.fetchAPI<PeriodSettings>(`/api/PeriodManagement/initialize${queryString}`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('[PeriodManagementService] initializePeriodSettings failed:', error);
      throw error;
    }
  }
}

export const periodManagementService = new PeriodManagementService();
